/**
 * Custom hook for managing trips with API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tripsAPI, shipmentsAPI, customersAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

const API_BASE_URL = 'https://sourcemygoods.com/api/public';

// Helper function to send emails
async function sendEmail(endpoint: string, data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error(`Failed to send ${endpoint}:`, await response.text());
    }
  } catch (error) {
    console.error(`Error sending ${endpoint}:`, error);
  }
}

export function useTrips() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['trips'],
    queryFn: async () => {
      const response = await tripsAPI.getAll();
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: tripsAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      toast({ title: 'Success', description: 'Trip created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: tripsAPI.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      toast({ title: 'Success', description: 'Trip updated successfully' });
      
      // Email notifications are handled by useShipments hook
      // When trip status changes, Trips.tsx calls updateShipment() for each shipment
      // That triggers useShipments which sends the emails
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: tripsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      toast({ title: 'Success', description: 'Trip deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    trips: data || [],
    isLoading,
    error,
    createTrip: createMutation.mutate,
    updateTrip: updateMutation.mutate,
    deleteTrip: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
